import { useEffect, useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import { FileText, Loader2 } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useNotifications } from '../store/useStore';
import { COMPANY_INFO } from '../lib/letterheadPdf';
import { formatCurrency, getDaysUntilRenewal } from '../lib/subscriptionUtils';
import {
    SUBSCRIPTION_MONTH_OPTIONS,
    createSubscriptionInvoice,
    getSubscriptionInvoicePeriod,
    getSuggestedInvoiceMonths,
    previewSubscriptionInvoiceTotals,
} from '../lib/subscriptionInvoiceUtils';
import type { CustomerSubscription } from './SubscriptionFormModal';

interface Props {
    open: boolean;
    onClose: () => void;
    onCreated: () => void;
    subscription: CustomerSubscription | null;
}

const todayISO = () => new Date().toISOString().slice(0, 10);

const formatLongDate = (isoDate: string) => format(parseISO(isoDate), 'd MMMM yyyy', { locale: it });

export function SubscriptionInvoiceModal({ open, onClose, onCreated, subscription }: Props) {
    const { addNotification } = useNotifications();

    const [months, setMonths] = useState(1);
    const [periodStart, setPeriodStart] = useState(todayISO());
    const [invoiceDate, setInvoiceDate] = useState(todayISO());
    const [status, setStatus] = useState<'draft' | 'issued'>('issued');
    const [noVatForfettario, setNoVatForfettario] = useState(true);
    const [advanceRenewal, setAdvanceRenewal] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open || !subscription) return;
        setMonths(getSuggestedInvoiceMonths(subscription.next_renewal_date));
        setPeriodStart(subscription.next_renewal_date);
        setInvoiceDate(todayISO());
        setStatus('issued');
        setNoVatForfettario(true);
        setAdvanceRenewal(true);
    }, [open, subscription]);

    const period = useMemo(
        () => getSubscriptionInvoicePeriod(periodStart || todayISO(), months),
        [periodStart, months]
    );

    const totals = useMemo(() => {
        if (!subscription) return null;
        return previewSubscriptionInvoiceTotals(subscription, months, period, noVatForfettario);
    }, [subscription, months, period, noVatForfettario]);

    const overdueDays = subscription ? getDaysUntilRenewal(subscription.next_renewal_date) : 0;

    const handleSubmit = async () => {
        if (!subscription || submitting) return;
        setSubmitting(true);
        try {
            const result = await createSubscriptionInvoice({
                subscription,
                months,
                periodStart,
                invoiceDate,
                status,
                noVatForfettario,
                iban: COMPANY_INFO.iban,
                advanceRenewal,
            });

            addNotification({
                type: 'success',
                title: 'Fattura creata',
                message: `${result.invoice.invoice_number ?? 'Bozza'} - ${months} ${months === 1 ? 'mese' : 'mesi'} di abbonamento.`,
            });

            if (result.renewalError) {
                addNotification({
                    type: 'error',
                    title: 'Rinnovo non aggiornato',
                    message: `Fattura creata, ma la data di rinnovo non è stata spostata: ${result.renewalError}`,
                });
            }

            onCreated();
            onClose();
        } catch (err: any) {
            addNotification({
                type: 'error',
                title: 'Errore creazione fattura',
                message: err?.message ?? 'Impossibile creare la fattura',
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v && !submitting) onClose(); }}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <FileText className="w-5 h-5" /> Fattura abbonamento
                    </DialogTitle>
                    <DialogDescription>
                        {subscription?.customers?.company_name ?? 'Cliente'} — {formatCurrency(subscription?.monthly_amount ?? 0)}/mese
                    </DialogDescription>
                </DialogHeader>

                {subscription && (
                    <div className="space-y-4 py-1">
                        {overdueDays < 0 && (
                            <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                                Scaduta da {Math.abs(overdueDays)} giorni (rinnovo previsto il {formatLongDate(subscription.next_renewal_date)}).
                            </div>
                        )}

                        {/* Mesi da fatturare */}
                        <div className="space-y-1">
                            <Label>Mesi da fatturare *</Label>
                            <Select value={String(months)} onValueChange={(v) => setMonths(Number(v))}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {SUBSCRIPTION_MONTH_OPTIONS.map((m) => (
                                        <SelectItem key={m} value={String(m)}>
                                            {m} {m === 1 ? 'mese' : 'mesi'} — {formatCurrency(subscription.monthly_amount * m)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Periodo */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="space-y-1">
                                <Label htmlFor="periodStart">Inizio periodo</Label>
                                <Input
                                    id="periodStart"
                                    type="date"
                                    value={periodStart}
                                    onChange={(e) => setPeriodStart(e.target.value)}
                                />
                            </div>
                            <div className="space-y-1">
                                <Label>Fine periodo</Label>
                                <div className="h-10 flex items-center text-sm text-gray-700">
                                    {formatLongDate(period.end)}
                                </div>
                            </div>
                        </div>

                        {/* Data fattura + stato */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="space-y-1">
                                <Label htmlFor="invoiceDate">Data fattura</Label>
                                <Input
                                    id="invoiceDate"
                                    type="date"
                                    value={invoiceDate}
                                    onChange={(e) => setInvoiceDate(e.target.value)}
                                />
                            </div>
                            <div className="space-y-1">
                                <Label>Stato</Label>
                                <Select value={status} onValueChange={(v) => setStatus(v as 'draft' | 'issued')}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="draft">Bozza (no numero)</SelectItem>
                                        <SelectItem value="issued">Emessa (numera)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {/* Opzioni */}
                        <div className="space-y-2">
                            <label className="flex items-start gap-2 text-sm cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={noVatForfettario}
                                    onChange={(e) => setNoVatForfettario(e.target.checked)}
                                    className="mt-0.5 h-4 w-4 rounded border-gray-300"
                                />
                                <span>
                                    <span className="font-medium">Regime forfettario (senza IVA)</span>
                                    <span className="block text-xs text-gray-500">
                                        Se disattivo, viene applicata IVA 22%.
                                    </span>
                                </span>
                            </label>

                            <label className="flex items-start gap-2 text-sm cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={advanceRenewal}
                                    onChange={(e) => setAdvanceRenewal(e.target.checked)}
                                    className="mt-0.5 h-4 w-4 rounded border-gray-300"
                                />
                                <span>
                                    <span className="font-medium">Sposta il prossimo rinnovo al {formatLongDate(period.end)}</span>
                                    <span className="block text-xs text-gray-500">
                                        Aggiorna la subscription con il periodo appena fatturato.
                                    </span>
                                </span>
                            </label>
                        </div>

                        {/* Riepilogo */}
                        {totals && (
                            <div className="rounded border bg-gray-50 p-3 space-y-1 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Imponibile ({months} × {formatCurrency(subscription.monthly_amount)})</span>
                                    <span className="font-mono">{formatCurrency(totals.taxable_amount)}</span>
                                </div>
                                {totals.tax_amount > 0 && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">IVA</span>
                                        <span className="font-mono">{formatCurrency(totals.tax_amount)}</span>
                                    </div>
                                )}
                                {totals.marca_da_bollo_amount > 0 && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Marca da bollo</span>
                                        <span className="font-mono">{formatCurrency(totals.marca_da_bollo_amount)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between border-t pt-1 font-semibold">
                                    <span>Totale fattura</span>
                                    <span className="font-mono text-green-700">{formatCurrency(totals.total_amount)}</span>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={submitting}>Annulla</Button>
                    <Button onClick={handleSubmit} disabled={submitting || !subscription}>
                        {submitting ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creazione...</>
                        ) : (
                            `Crea fattura ${months} ${months === 1 ? 'mese' : 'mesi'}`
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default SubscriptionInvoiceModal;
